import React, { useState } from 'react';
import { Categories } from './categories';
import { Route, Redirect, Switch, useLocation } from 'react-router-dom';
import { NavBar, visibleNavItems } from './navbar';
import { Category } from './category';
import { UpsertCatalogItem } from './catalog/upsertItem';
import { CreateCategory } from './category/create';
import { EditCategory } from './category/edit';
import { CategoryTree } from './category/CategoryTree';
import { CreateRetailer } from './retailers/create';
import { UploadAssets } from "./uploadAssets";
import { FullReport } from "./report";
import { Economy } from "./economy";
import { UsersFeedback } from "./usersFeedback";
import { ContentReports } from "./contentReports";
import { AvatarItems } from "./avatarItems";
import { RenderPerformance } from "./renderPerformance";
import { Challenges } from "./challenges";
import { CreateChallenge } from "./challenges/create";
import { EditChallenge } from "./challenges/edit";
import { ChallengeResults } from "./challenges/results";
import { Notifications } from "./notifications";
import { Campaigns } from "./campaigns";
import { CreateCampaign } from "./campaigns/create";
import { EditCampaign } from "./campaigns/edit";
import { GeoExposureAnswers } from "./geoExposure";
import { GeoExposureQueries } from "./geoExposure/queries";
import { GeoExposureSettings } from "./geoExposure/settings";
import { GrantCredits } from "./grantCredits";
import { Templates } from "./templates";
import { Dashboard } from "./dashboard";
import { ModelsInspector } from "./modelsInspector";
import { IngestionPipeline } from "./ingestion";
import { IngestionCreate } from "./ingestion/create";
import { IngestionDetail } from "./ingestion/detail";
import { IngestionCosts } from "./ingestion/costs";
import { ExtensionPage } from "./extension";
import { AdminUsers } from "./adminUsers";
import { RenderExperiments } from "./renderExperiments";
import { RenderExperimentForm } from "./renderExperiments/ExperimentForm";
import { RenderExperimentDetail } from "./renderExperiments/ExperimentDetail";
import { RenderExperimentRate } from "./renderExperiments/RatePage";
import { RenderExperimentResults } from "./renderExperiments/ResultsPage";
import { Layout, Drawer, Grid, Button, Result } from 'antd';
import { MenuOutlined } from '@ant-design/icons';
import { observer } from 'mobx-react';
import { useCurrentUser } from '../user/permissions';
import { recordPageVisit } from './pageUsage';
import './index.css';

const { Sider, Content } = Layout;
const { useBreakpoint } = Grid;
const SIDER_WIDTH = 280;

const NoAccess = () => (
    <Result status="403" title="You don't have access to this page" subTitle="Ask an admin to add the permission on the Users page." />
);

/** A Route that renders only for users holding `scope` (full admin when omitted). */
const ScopedRoute = observer(({ scope = '*', ownership, component: Component, ...rest }) => {
    const currentUserStore = useCurrentUser();
    return (
        <Route {...rest} render={(props) => currentUserStore.can(scope, ownership) ? <Component {...props} /> : <NoAccess />} />
    );
});

const Home = observer(() => {
    const home = visibleNavItems(useCurrentUser())[0];
    return home ? <Redirect to={home.key} /> : <NoAccess />;
});

export const App = () => {
    const screens = useBreakpoint();
    const isMobile = !screens.lg;
    const [drawerOpen, setDrawerOpen] = useState(false);
    const location = useLocation();
    const uid = useCurrentUser().user?.uid;

    React.useEffect(() => { setDrawerOpen(false); }, [location.pathname]);
    React.useEffect(() => { recordPageVisit(uid, location.pathname); }, [uid, location.pathname]);

    return (
        <Layout style={{ minHeight: '100vh' }}>
            {!isMobile && (
                <Sider width={SIDER_WIDTH} style={{ position: 'fixed', height: '100vh', left: 0, top: 0, zIndex: 100 }}>
                    <NavBar/>
                </Sider>
            )}

            {isMobile && (
                <Drawer
                    placement="left"
                    width={SIDER_WIDTH}
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    closable={false}
                    bodyStyle={{ padding: 0, background: '#001529' }}
                    headerStyle={{ display: 'none' }}
                >
                    <NavBar/>
                </Drawer>
            )}

            <Layout style={{ marginLeft: isMobile ? 0 : SIDER_WIDTH }}>
                {isMobile && (
                    <Button
                        type="default"
                        shape="circle"
                        icon={<MenuOutlined />}
                        onClick={() => setDrawerOpen(true)}
                        aria-label="Open menu"
                        style={{ position: 'fixed', top: 12, left: 12, zIndex: 99 }}
                    />
                )}

                <Content style={{ padding: 0, minHeight: '100vh', overflowY: 'auto' }}>
                    <Switch>
                        <Route exact path="/" component={Home} />
                        <Route path="/dashboard" component={Dashboard}/>
                        <ScopedRoute exact path="/catalog" component={Categories}/>
                        <ScopedRoute path="/catalog/create" component={UpsertCatalogItem}/>
                        <ScopedRoute path="/catalog/update/:id" component={UpsertCatalogItem}/>
                        <ScopedRoute path="/assets/:id" component={UploadAssets}/>
                        <ScopedRoute path="/catalog/category/:id" component={Category}/>
                        <ScopedRoute exact path="/category" component={CategoryTree}/>
                        <ScopedRoute path="/category/new" component={CreateCategory}/>
                        <ScopedRoute path="/category/:id/edit" component={EditCategory}/>
                        <ScopedRoute path="/retailer" component={CreateRetailer}/>
                        <ScopedRoute path="/report" scope="upload-pipeline.read" ownership="all" component={FullReport}/>
                        <ScopedRoute path="/economy" component={Economy}/>
                        <ScopedRoute path="/content-reports" component={ContentReports}/>
                        <ScopedRoute path="/avatar-items" component={AvatarItems}/>
                        <ScopedRoute path="/render-performance" component={RenderPerformance}/>
                        <ScopedRoute exact path="/experiments" component={RenderExperiments}/>
                        <ScopedRoute path="/experiments/new" component={RenderExperimentForm}/>
                        <ScopedRoute path="/experiments/:id/edit" component={RenderExperimentForm}/>
                        <ScopedRoute path="/experiments/:id/rate" component={RenderExperimentRate}/>
                        <ScopedRoute path="/experiments/:id/results" component={RenderExperimentResults}/>
                        <ScopedRoute path="/experiments/:id" component={RenderExperimentDetail}/>
                        <ScopedRoute exact path="/challenges" component={Challenges}/>
                        <ScopedRoute path="/challenges/create" component={CreateChallenge}/>
                        <ScopedRoute path="/challenges/edit/:id" component={EditChallenge}/>
                        <ScopedRoute path="/challenges/results/:id" component={ChallengeResults}/>
                        <ScopedRoute exact path="/templates" component={Templates}/>
                        <ScopedRoute path="/notifications" component={Notifications}/>
                        <ScopedRoute exact path="/campaigns" scope="campaigns.manage" component={Campaigns}/>
                        <ScopedRoute path="/campaigns/new" scope="campaigns.manage" component={CreateCampaign}/>
                        <ScopedRoute path="/campaigns/edit/:id" scope="campaigns.manage" component={EditCampaign}/>
                        <ScopedRoute exact path="/geo-exposure" scope="geo-exposure.manage" component={GeoExposureAnswers}/>
                        <ScopedRoute path="/geo-exposure/queries" scope="geo-exposure.manage" component={GeoExposureQueries}/>
                        <ScopedRoute path="/geo-exposure/settings" scope="geo-exposure.manage" component={GeoExposureSettings}/>
                        <ScopedRoute path="/grant-credits" component={GrantCredits}/>
                        <ScopedRoute path="/feedback" component={UsersFeedback}/>
                        <ScopedRoute path="/models-inspector" component={ModelsInspector}/>
                        <ScopedRoute exact path="/ingestion" scope="upload-pipeline.read" component={IngestionPipeline}/>
                        <ScopedRoute path="/ingestion/new" scope="upload-pipeline.write" component={IngestionCreate}/>
                        <ScopedRoute path="/ingestion/costs" scope="upload-pipeline.read" component={IngestionCosts}/>
                        <ScopedRoute path="/ingestion/:id" scope="upload-pipeline.read" component={IngestionDetail}/>
                        <ScopedRoute path="/extension" scope="upload-pipeline.write" component={ExtensionPage}/>
                        <ScopedRoute path="/users" scope="admin-users.manage" component={AdminUsers}/>
                    </Switch>
                </Content>
            </Layout>
        </Layout>
    );
};
